import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../api/axios';

const ReviewPage = () => {
  const [searchParams] = useSearchParams();
  const tableNum = searchParams.get('table');
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (rating === 0) { setError('Veuillez donner une note'); return; }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/reviews', {
        rating,
        comment,
        name,
        table_number: tableNum ? parseInt(tableNum) : null,
      });
      setSubmitted(true);
    } catch {
      setError('Erreur lors de l\'envoi. Veuillez reessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f5f1ed] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="review-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#2c1810] mb-2">Merci !</h2>
          <p className="text-stone-600 mb-6">Votre avis a bien ete enregistre. Nous vous remercions pour votre retour.</p>
          <div className="flex justify-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} size={28} className={s <= rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'} />
            ))}
          </div>
          <a href="/" className="inline-block mt-4 text-sm text-[#2c1810] underline hover:no-underline">
            Retour au site La Taverne
          </a>
        </div>
      </div>
    );
  }

  const ratingLabels = ['', 'Decevant', 'Moyen', 'Bien', 'Tres bien', 'Excellent'];

  return (
    <div className="min-h-screen bg-[#f5f1ed] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-md w-full" data-testid="review-form">
        {/* Header */}
        <div className="bg-[#2c1810] text-white p-6 text-center">
          <img src="/logo-taverne.png" alt="La Taverne" className="h-12 mx-auto mb-3" />
          <h1 className="text-xl font-bold">Votre avis compte</h1>
          <p className="text-xs text-gray-300 mt-1">
            {tableNum ? `Table ${tableNum} - ` : ''}Comment etait votre experience ?
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Star Rating */}
          <div className="text-center">
            <p className="text-sm font-semibold text-stone-600 mb-3">Notez votre experience</p>
            <div className="flex justify-center gap-2" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  onClick={() => setRating(s)}
                  onMouseEnter={() => setHoveredStar(s)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="transition-transform hover:scale-125 focus:outline-none"
                  data-testid={`star-${s}`}
                >
                  <Star
                    size={40}
                    className={`transition-colors ${
                      s <= (hoveredStar || rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-stone-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            {(hoveredStar || rating) > 0 && (
              <p className="text-sm mt-2 font-medium text-amber-600" data-testid="rating-label">
                {ratingLabels[hoveredStar || rating]}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="text-sm font-semibold text-stone-600 block mb-1">Commentaire (optionnel)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Partagez votre experience..."
              rows={3}
              maxLength={500}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2c1810] resize-none"
              data-testid="review-comment"
            />
            <p className="text-right text-[10px] text-stone-400">{comment.length}/500</p>
          </div>

          {/* Name */}
          <div>
            <label className="text-sm font-semibold text-stone-600 block mb-1">Votre prenom (optionnel)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Jean"
              maxLength={100}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2c1810]"
              data-testid="review-name"
            />
          </div>

          {error && <p className="text-red-500 text-xs text-center" data-testid="review-error">{error}</p>}

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-[#2c1810] hover:bg-[#3d2817] text-white py-3"
            data-testid="submit-review-btn"
          >
            {submitting ? 'Envoi...' : <><Send size={16} className="mr-2" /> Envoyer mon avis</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
